import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Tooltip,
  Typography
} from '@mui/material'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

// Icons
import AccessTimeIcon from '@mui/icons-material/AccessTime';

// SCSS
import styles from './methodPayment.module.scss'

// Utils
import { Iinvoice } from '../../../utils/types'
import { BASE_URL, STRIPE_PAYMENT_TYPE } from '../../../utils/constants'

// Components
import { LoadingSkeleton } from './LoadingSkeleton'

// Axios
import axios from '../../../providers/axios.config'
import Notiflix from 'notiflix';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/reducers/rootReducer';

interface IProps {
  open: boolean
  handleOpen: () => void
  handleConfirm: (key?: string) => void
  dialogTitle: string
  onClose?: () => void;
  selectedPlan: { qty: number; price: number; name: string; description: string; id: string | undefined; is_subscription: boolean; }
}

interface FormDataInvoice extends Iinvoice {
  user_subscription_uuid: string;
}

export const MethodPayment = (props: IProps) => {
  const isAuthenticated = useSelector((state: RootState) => state.user.isAuthenticated)
  const { open, handleOpen, handleConfirm, dialogTitle, onClose } = props
  const { t } = useTranslation()
  const [formData, setFormData] = useState<{ loading: boolean; invoice: FormDataInvoice | null; error: boolean; }>({
    loading: true,
    invoice: null,
    error: false
  })

  const getLastUnpaidInvoice = async () => {
    axios.get('invoices/last-unpaid', { params: { is_subscription: props.selectedPlan.is_subscription ? 1 : 0 } }).then((resp) => {
      setTimeout(() => {
        setFormData({ loading: false, invoice: resp.data, error: false })
      }, 500)
    }).catch((e) => {
      setFormData({ loading: false, invoice: null, error: true });
    });
  }

  useEffect(() => {
    getLastUnpaidInvoice();
  }, [])

  const handlePay = async (userSubscriptionUuid?: string) => {
    if(isAuthenticated) {
      try {
        const requestData: {
          plan: string;
          payment_method: string;
          url: string;
          user_subscription_uuid?: string;
        } = {
          plan: props.selectedPlan?.id || '',
          payment_method: STRIPE_PAYMENT_TYPE,
          url: `${BASE_URL}plans-prices?plan`
        };
        if (userSubscriptionUuid !== undefined) {
          requestData.user_subscription_uuid = userSubscriptionUuid;
        }
        const response = await axios.post(`subscriptions`, {...requestData}) as any
        if(response && typeof response === 'string') {
          window.open(response,"_self")
        }
        // if(payment === 'paypal' && response?.links?.length) {
        //   window.open(response?.links[1].href, '_self')
        // }
      } catch (error: any) {
        Notiflix.Report.failure(
          'Error',
          `${error?.response?.data?.message}`,
          'OK'
        )
      }
    }
    if (typeof props.onClose !== 'undefined') {
      props.onClose()
    }
  }

  const handlDeleteInvoice = async () => {
    if(isAuthenticated) {
      try {
        const response = await axios.delete(`invoices/${formData.invoice?.id}/delete`) as any
        if (response.status === 'success') {
          setFormData({ ...formData, invoice: null });
        }
        if (response.status === 'error') {
           Notiflix.Report.failure(
            'Error',
            `${response.message}`,
            'OK'
          )
        }
      } catch (error: any) {
        Notiflix.Report.failure(
          'Error',
          `${error?.response?.data?.message}`,
          'OK'
        )
      }
    }
  }

  const textBold = (text?: string) => (
    <Box
      component='span'
      sx={{ fontWeight: 'bold' }}
    >
      {text}
    </Box>
  )
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <div className={styles.content}>
          <Box display={'flex'} flexDirection={'column'} gap={3}>
            <Typography fontSize={15}>{t('modal_description_one_buy_plan')}</Typography>
            <Typography paragraph fontSize={15}>{t('modal_description_second_buy_plan')}</Typography>
          </Box>
          {!formData.error && (
            <Box>
              <Typography fontWeight={'bold'} fontSize={14}>{t('Selected plan')}</Typography>
              <Paper sx={{ padding: 2 }}>
                <Typography sx={{ fontSize: 14 }} color='text.secondary' gutterBottom>
                  {t('Plan')}: {textBold(t(`${props.selectedPlan?.name}`))}
                </Typography>
                <Typography sx={{ fontSize: 14 }} color='text.secondary' gutterBottom>
                  {t('price')}: {textBold(`${props.selectedPlan?.price} EUR`)}
                </Typography>
                <Typography sx={{ fontSize: 14 }} color='text.secondary' gutterBottom>
                  {textBold(`${props.selectedPlan?.qty} ${!props.selectedPlan.is_subscription ? t('Credits') : t('Images')}`)}
                </Typography>
              </Paper>
            </Box>
          )}
          <Box display={'flex'} flexDirection={'column'} gap={3}>
            {formData.loading && <Typography fontSize={12} variant="caption">{`${t('checking_eligibility_payment')}...`}</Typography>}
            {formData.loading && <LoadingSkeleton />}
          </Box>
          {!formData.loading && !formData.error && formData.invoice && (
            <Alert variant="outlined" severity="warning">
              <AlertTitle>{t('an unpaid invoice was found')}</AlertTitle>
              <Typography fontSize={14} fontWeight={'bold'}>{t('modal_description_unpaid_invoice_found_at_buy_plan')}</Typography>
              <Box display={'flex'} flexDirection={'row'} gap={1} flexWrap={'wrap'} alignItems={'center'}>
                <Box fontSize={12} display={'flex'} alignItems={'center'}>
                  <Box fontWeight={'bold'} fontSize={12}>{t('Plan')}:</Box>
                  &nbsp;
                  <Tooltip title={formData.invoice.product_name} placement="top-start">
                    <Typography
                      fontSize={12}
                      component="span"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          width: 200
                        }}
                      >
                        {formData.invoice.product_name}
                      </Typography>
                    </Tooltip>
                </Box>
                <Box fontSize={10}><Chip size="small" label={(<Typography fontSize={10}>{t(formData.invoice.status || '')}</Typography>)} color="warning" /></Box>
                <Box fontWeight={'bold'} fontSize={12}>{`${formData.invoice.total} EUR`}</Box>
                <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
                  <AccessTimeIcon fontSize='small' />
                  <Typography fontSize={12}>{`${moment(formData.invoice?.createdAt).add(48, 'hours').diff(moment(), 'hours')} ${t('hours left').toLowerCase()}`}</Typography>
                </Box>
              </Box>
              <Box display={'flex'} flexDirection={'row'} alignItems={'center'} justifyContent={'start'} gap={1}>
                <Button
                  variant='contained'
                  color='success'
                  className={styles.textfieldButton}
                  size='small'
                  onClick={() => formData.invoice ? handlePay(formData.invoice?.user_subscription_uuid) : {}}
                >
                  {`${t('pay')}`}
                </Button>
                <Button
                  variant='contained'
                  color='primary'
                  className={styles.textfieldButton}
                  size='small'
                  onClick={() => handlDeleteInvoice()}
                >
                  {t('Delete')}
                </Button>
              </Box>
            </Alert>
          )}
          {!formData.loading && formData.error && (
            <Alert severity="error">{t('error_checking_eligibility_payment')}</Alert>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant='outlined'>
          {t('Cancel')}
        </Button>
        <Button disabled={formData.loading || !!formData.invoice || formData.error} onClick={() => handlePay()} variant='contained' color='primary'>
          {`Buy (${props.selectedPlan.price} EUR)`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
